import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { FormSection } from '../../components/Form';
import { Client } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import LinkedEmailsSection from '../emails/components/LinkedEmailsSection';
import ClientContactsSection from '../client-contacts/components/ClientContactsSection';
import ActivityLogSection from '../activity-logs/components/ActivityLogSection';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';
import { DocumentTextIcon } from '../../components/ui/Icons';
import ContactPointsEditor from '../address-book/ContactPointsEditor';
import OrganizationContactsEditor from '../address-book/OrganizationContactsEditor';
import OrganizationPhonesEditor from '../address-book/OrganizationPhonesEditor';
import OrganizationContactsDisplay from '../address-book/OrganizationContactsDisplay';
import ContactPointsDisplay from '../address-book/ContactPointsDisplay';
import {
  ContactPointRow,
  contactPointsToRows,
  contactPointsFilterText,
  hasContactPoints,
  rowsToContactPoints,
} from '../address-book/contactPoints';
import {
  flattenOrganizationContacts,
  groupContactPointsToOrganizationContacts,
  emptyOrganizationContact,
  type OrganizationContactRow,
} from '../address-book/organizationContacts';
import type { ThemeColors } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { displayVatNumber, vatForFormField } from './vatUtils';
import {
  deleteActionClass,
  editActionClass,
  mutedTextClass,
  primaryLinkClass,
  secondaryLinkClass,
  summaryActionClass,
  viewActionClass,
} from '../address-book/themeHelpers';

type ClientFormData = Partial<Client> & {
  organizationContacts?: OrganizationContactRow[];
  contactEmails?: ContactPointRow[];
  contactPhones?: ContactPointRow[];
};

function prepareClientSaveData(data: ClientFormData) {
  const { organizationContacts, contactEmails, contactPhones, ...rest } = data;
  const emails = organizationContacts
    ? flattenOrganizationContacts(organizationContacts)
    : rowsToContactPoints(contactEmails, 'EMAIL');
  const phones = rowsToContactPoints(contactPhones, 'PHONE');
  const contactPoints = [...emails, ...phones].map((point, index) => ({
    ...point,
    sortOrder: index,
  }));
  return {
    ...rest,
    contactPoints,
  };
}

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Address Book',
    i18nKey: 'clients',
    permissionModuleKey: 'clients',
    route: {
      list: '/clients',
      new: '/clients/new',
      detail: (id: number) => `/clients/${id}`,
      edit: (id: number) => `/clients/${id}/edit`,
      summary: (id: number) => `/clients/${id}/summary`,
    },
    // API Endpoints
    api: {
      list: '/crm-clients',
      detail: (id: number) => `/crm-clients/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search organizations by name, email, or phone...',
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'clientName', 'clientName2', 'clientCode', 'contactPerson'
      ],
    },
    {
      name: 'contact',
      label: 'Contact',
      icon: '☎️',
      fields: [
        'clientEmail', 'clientPhone', 'clientWebSite'
      ],
    },
    {
      name: 'location',
      label: 'Location',
      icon: '📍',
      fields: [
        'clientAddress', 'clientCity', 'clientZipCode', 'clientState'
      ],
    },
    {
      name: 'business',
      label: 'Business',
      icon: '💼',
      fields: [
        'clientVATNumber', 'gemiNumber', 'clientPaymentTerms', 'dateStarted'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    // Filter field definitions
    fields: [
      {
        key: 'clientName',
      label: 'Organization name',
      placeholder: 'All names',
        getValue: (item: Client) => item.clientName,
        options: [],
      },
      {
        key: 'clientName2',
      label: 'Alternate name',
      placeholder: 'All alternate names',
        getValue: (item: Client) => item.clientName2,
        options: [],
      },
      {
        key: 'contactPerson',
        label: 'Contact Person',
        placeholder: 'All Contact Person',
        getValue: (item: Client) => item.contactPerson,
        options: [],
      },
      {
        key: 'clientEmail',
        label: 'Email',
        placeholder: 'All emails',
        getValue: (item: Client) => contactPointsFilterText(item.contactPoints, 'EMAIL'),
        options: [],
      },
      {
        key: 'clientPhone',
        label: 'Phone',
        placeholder: 'All phones',
        getValue: (item: Client) => contactPointsFilterText(item.contactPoints, 'PHONE'),
        options: [],
      },
      {
        key: 'clientAddress',
        label: 'Address',
        placeholder: 'Type to filter address...',
        type: 'text' as const,
        getValue: (item: Client) => item.clientAddress,
        options: [],
      },
      {
        key: 'clientCity',
        label: 'City',
        placeholder: 'All City',
        getValue: (item: Client) => item.clientCity,
        options: [],
      },
      {
        key: 'clientZipCode',
        label: 'Zip Code',
        placeholder: 'All Zip Code',
        getValue: (item: Client) => item.clientZipCode,
        options: [],
      },
      {
        key: 'clientState',
        label: 'State',
        placeholder: 'All State',
        getValue: (item: Client) => item.clientState,
        options: [],
      },
      {
        key: 'clientWebSite',
        label: 'Website',
        placeholder: 'All Website',
        getValue: (item: Client) => item.clientWebSite,
        options: [],
      },
      {
        key: 'clientVATNumber',
        label: 'VAT Number',
        placeholder: 'All VAT Number',
        getValue: (item: Client) => displayVatNumber(item.clientVATNumber),
        options: [],
      },
      {
        key: 'gemiNumber',
        label: 'GEMI Number',
        placeholder: 'All GEMI Number',
        getValue: (item: Client) => item.gemiNumber,
        options: [],
      },
      {
        key: 'clientCode',
        label: 'Code',
        placeholder: 'All Code',
        getValue: (item: Client) => item.clientCode,
        options: [],
      },
      {
        key: 'clientPaymentTerms',
        label: 'Payment Terms',
        placeholder: 'All Payment Terms',
        getValue: (item: Client) => item.clientPaymentTerms?.toString(),
        options: [],
      },
      {
        key: 'dateStarted',
        label: 'Date Started',
        placeholder: 'All Date Started',
        type: 'date' as const,
        getValue: (item: Client) =>
          item.dateStarted
            ? formatDateGlobal(item.dateStarted)
            : undefined,
        options: [],
      },
      {
        key: 'clientCreatedAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: Client) =>
          item.clientCreatedAt
            ? formatDateGlobal(item.clientCreatedAt)
            : undefined,
        options: [],
      },
      {
        key: 'clientUpdatedAt',
        label: 'Updated At',
        placeholder: 'All Updated At',
        type: 'date' as const,
        getValue: (item: Client) =>
          item.clientUpdatedAt
            ? formatDateGlobal(item.clientUpdatedAt)
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: Client[],
      filters: {
        clientName: string[];
        clientName2: string[];
        contactPerson: string[];
        clientEmail: string[];
        clientPhone: string[];
        clientAddress: string[] | string;
        clientCity: string[];
        clientZipCode: string[];
        clientState: string[];
        clientWebSite: string[];
        clientVATNumber: string[];
        gemiNumber: string[];
        clientCode: string[];
        clientPaymentTerms: string[];
        dateStarted: string[];
        clientCreatedAt: string[];
        clientUpdatedAt: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Client Name filter
        if (filters.clientName.length > 0) {
          const value = (item.clientName || '').toLowerCase();
          const matches = filters.clientName.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Client Name 2 filter
        if (filters.clientName2.length > 0) {
          const value = (item.clientName2 || '').toLowerCase();
          const matches = filters.clientName2.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Contact Person filter
        if (filters.contactPerson.length > 0) {
          const value = (item.contactPerson || '').toLowerCase();
          const matches = filters.contactPerson.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Email filter
        if (filters.clientEmail.length > 0) {
          const value = contactPointsFilterText(item.contactPoints, 'EMAIL');
          const matches = filters.clientEmail.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Phone filter
        if (filters.clientPhone.length > 0) {
          const value = contactPointsFilterText(item.contactPoints, 'PHONE');
          const matches = filters.clientPhone.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Address filter (text filter)
        const addressFilter = filters.clientAddress;
        if (typeof addressFilter === 'string' && addressFilter.trim() !== '') {
          const value = (item.clientAddress || '').toLowerCase();
          if (!value.includes(addressFilter.toLowerCase())) return false;
        } else if (Array.isArray(addressFilter) && addressFilter.length > 0) {
          const value = (item.clientAddress || '').toLowerCase();
          const matches = addressFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // City filter
        if (filters.clientCity.length > 0) {
          const value = (item.clientCity || '').toLowerCase();
          const matches = filters.clientCity.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Zip Code filter
        if (filters.clientZipCode.length > 0) {
          const value = (item.clientZipCode || '').toLowerCase();
          const matches = filters.clientZipCode.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // State filter
        if (filters.clientState.length > 0) {
          const value = (item.clientState || '').toLowerCase();
          const matches = filters.clientState.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Website filter
        if (filters.clientWebSite.length > 0) {
          const value = (item.clientWebSite || '').toLowerCase();
          const matches = filters.clientWebSite.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // VAT Number filter
        if (filters.clientVATNumber.length > 0) {
          const value = (item.clientVATNumber || '').toLowerCase();
          const matches = filters.clientVATNumber.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // GEMI Number filter
        if (filters.gemiNumber.length > 0) {
          const value = (item.gemiNumber || '').toLowerCase();
          const matches = filters.gemiNumber.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Code filter
        if (filters.clientCode.length > 0) {
          const value = (item.clientCode || '').toLowerCase();
          const matches = filters.clientCode.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Payment Terms filter
        if (filters.clientPaymentTerms.length > 0) {
          const value = item.clientPaymentTerms?.toString() || '';
          const matches = filters.clientPaymentTerms.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Date Started filter
        if (filters.dateStarted.length > 0) {
          if (ranges.dateStarted) {
            const range = ranges.dateStarted;
            if (range.start && range.end && item.dateStarted) {
              const itemDate = new Date(item.dateStarted);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.dateStarted
              ? formatDateGlobal(item.dateStarted)
              : '';
            const matches = filters.dateStarted.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Created At filter
        if (filters.clientCreatedAt.length > 0) {
          if (ranges.clientCreatedAt) {
            const range = ranges.clientCreatedAt;
            if (range.start && range.end && item.clientCreatedAt) {
              const itemDate = new Date(item.clientCreatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientCreatedAt
              ? formatDateGlobal(item.clientCreatedAt)
              : '';
            const matches = filters.clientCreatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Updated At filter
        if (filters.clientUpdatedAt.length > 0) {
          if (ranges.clientUpdatedAt) {
            const range = ranges.clientUpdatedAt;
            if (range.start && range.end && item.clientUpdatedAt) {
              const itemDate = new Date(item.clientUpdatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientUpdatedAt
              ? formatDateGlobal(item.clientUpdatedAt)
              : '';
            const matches = filters.clientUpdatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-csv',
            label: 'Export to CSV',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToCSV } = await import('../../services/exportService');
                await exportToCSV(exportData);
              } catch (error: any) {
                console.error('Failed to export to CSV:', error);
                throw new Error(error?.message || 'Failed to export to CSV. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-pdf',
            label: 'Export to PDF',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToPDF } = await import('../../utils/exportToPdf');
                await exportToPDF(exportData);
              } catch (error: any) {
                console.error('Failed to export to PDF:', error);
                throw new Error(error?.message || 'Failed to export to PDF. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add organization',
        showTitle: false,
        variant: 'primary' as const,
        href: '/clients/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Client) => {
      window.location.href = `/clients/${item.clientId}`; // Using direct route, can be changed to use module.route.detail if needed
    },
    keyExtractor: (item: Client) => item.clientId,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by adding an organization.',
    noSearchResults: 'No organizations found matching your search criteria.',
    noFilterResults: 'No organizations match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete organization',
    message: 'Are you sure you want to delete this organization? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete organization. Please try again.',
    handler: async (id: number) => {
      const { deleteClient } = await import('../../services/mutations');
      await deleteClient(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Organization details',
    notFoundTitle: 'Organization not found',
    notFoundMessage: 'The organization you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to address book',
    defaultTitle: 'Unnamed organization',
    getTitle: (item: Client) => item.clientName || 'Unnamed organization',
  },

  // Form Configuration
  form: {
    new: {
      title: 'New organization',
      subtitle: 'Add a company or group to your address book',
      submitText: 'Create organization',
    },
    edit: {
      title: 'Edit organization',
      subtitle: 'Update organization information',
      submitText: 'Update organization',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading organization...',
    errorMessage: 'Failed to save organization. Please try again.',
    contentMaxWidthClass: 'max-w-7xl',
    supportsFiles: true,
    create: async (data: Partial<Client>, files?: File[], filesToDetach?: number[]) => {
      const { createClient } = await import('../../services/mutations');
      const formData = data as ClientFormData;
      return await createClient(prepareClientSaveData(formData), files);
    },
    update: async (id: number, data: Partial<Client>, files?: File[], filesToDetach?: number[]) => {
      const { updateClient } = await import('../../services/mutations');
      const formData = data as ClientFormData;
      return await updateClient(id, prepareClientSaveData(formData), files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) =>
      `/crm-clients/client/${entityId}/files/${fileId}`,
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientName',
      label: 'Organization name',
      type: 'text',
      required: true,
      placeholder: 'Enter organization name',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'clientName2',
      label: 'Alternate name',
      type: 'text',
      placeholder: 'Enter alternate name',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'clientCode',
      label: 'Reference code',
      type: 'text',
      placeholder: 'Enter reference code',
      validation: {
        maxLength: 100,
      },
    },
    {
      name: 'organizationContacts',
      label: '',
      type: 'custom',
      customRender: ({ value, onChange }) => (
        <OrganizationContactsEditor
          value={(value as OrganizationContactRow[]) ?? []}
          onChange={(contacts) => onChange(contacts)}
        />
      ),
    },
    {
      name: 'contactEmails',
      label: '',
      type: 'custom',
      customRender: ({ value, onChange }) => (
        <ContactPointsEditor
          kind="EMAIL"
          label="Emails"
          value={(value as ContactPointRow[]) ?? []}
          onChange={onChange}
        />
      ),
    },
    {
      name: 'contactPhones',
      label: '',
      type: 'custom',
      customRender: ({ value, onChange }) => (
        <OrganizationPhonesEditor
          value={(value as ContactPointRow[]) ?? []}
          onChange={onChange}
        />
      ),
    },
    {
      name: 'clientWebSite',
      label: 'Website',
      type: 'url',
      placeholder: 'Enter website URL',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'clientAddress',
      label: 'Address',
      type: 'text',
      placeholder: 'Enter street address',
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'clientCity',
      label: 'City',
      type: 'text',
      placeholder: 'Enter city',
      validation: {
        maxLength: 100,
      },
    },
    {
      name: 'clientZipCode',
      label: 'Zip Code',
      type: 'text',
      placeholder: 'Enter zip code',
      validation: {
        maxLength: 20,
      },
    },
    {
      name: 'clientState',
      label: 'State',
      type: 'text',
      placeholder: 'Enter state',
      validation: {
        maxLength: 100,
      },
    },
    {
      name: 'clientVATNumber',
      label: 'VAT Number',
      type: 'text',
      placeholder: 'Enter VAT number',
      validation: {
        maxLength: 50,
      },
    },
    {
      name: 'gemiNumber',
      label: 'GEMI Number',
      type: 'text',
      placeholder: 'Enter GEMI number',
      validation: {
        maxLength: 50,
      },
    },
    {
      name: 'sourceUrl',
      label: 'Source URL',
      type: 'url',
      placeholder: 'Enter source URL',
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'clientPaymentTerms',
      label: 'Payment Terms',
      type: 'number',
      placeholder: 'Enter payment terms (days)',
      validation: {
        min: 0,
      },
    },
    {
      name: 'clientNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter notes',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📄',
        gridCols: 'md:grid-cols-12',
        fields: ['clientName', 'clientName2', 'clientCode'],
        fieldSpans: {
          clientName: 'md:col-span-5',
          clientName2: 'md:col-span-5',
          clientCode: 'md:col-span-2',
        },
        collapsible: true,
      },
      {
        id: 'contact',
        title: 'Contact Information',
        icon: '☎️',
        gridCols: 'md:grid-cols-12',
        fields: ['organizationContacts', 'contactPhones', 'clientWebSite'],
        fieldSpans: {
          organizationContacts: 'md:col-span-12',
          contactPhones: 'md:col-span-12',
          clientWebSite: 'md:col-span-12',
        },
        collapsible: true,
      },
      {
        id: 'address',
        title: 'Address Information',
        icon: '📍',
        gridCols: 'md:grid-cols-12',
        fields: ['clientAddress', 'clientCity', 'clientZipCode', 'clientState'],
        fieldSpans: {
          clientAddress: 'md:col-span-12',
          clientCity: 'md:col-span-6',
          clientZipCode: 'md:col-span-3',
          clientState: 'md:col-span-3',
        },
        collapsible: true,
      },
      {
        id: 'additional',
        title: 'Additional Information',
        icon: '📋',
        gridCols: 'md:grid-cols-12',
        fields: ['clientVATNumber', 'gemiNumber', 'sourceUrl', 'clientPaymentTerms', 'clientNotes'],
        fieldSpans: {
          clientVATNumber: 'md:col-span-6',
          gemiNumber: 'md:col-span-6',
          sourceUrl: 'md:col-span-12',
          clientPaymentTerms: 'md:col-span-6',
          clientNotes: 'md:col-span-12',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns (needs to be in a separate function to use JSX)
export const getColumns = (
  formatDate: (dateString?: string) => string,
  _opts?: unknown,
  colors?: ThemeColors,
): Column<Client>[] => [
  {
    key: 'clientName',
    header: 'Organization',
    sortable: true,
    render: (value, row) => {
      const attachmentCount = row.FileAttachment?.length ?? row._count?.FileAttachment ?? 0;
      const orgName = row.clientName || 'Unnamed organization';
      const truncatedName = orgName.length > 50 ? orgName.substring(0, 50) + '...' : orgName;

      return (
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate">
              {displayVatNumber(row.clientVATNumber)
                ? `${truncatedName} (${displayVatNumber(row.clientVATNumber)})`
                : truncatedName}
            </span>
            {attachmentCount > 0 && (
              <DocumentTextIcon className={cn('w-4 h-4 flex-shrink-0', mutedTextClass(colors))} />
            )}
          </div>
          {row.contactPerson && (
            <div className={cn('mt-0.5 text-xs truncate', mutedTextClass(colors))}>
              {row.contactPerson}
              {row.contactPosition ? ` · ${row.contactPosition}` : ''}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'clientEmail',
    header: 'Email',
    sortable: true,
    render: (_value, row) => (
      <ContactPointsDisplay
        points={row.contactPoints}
        kind="EMAIL"
        compact
      />
    ),
  },
  {
    key: 'clientPhone',
    header: 'Phone',
    sortable: true,
    render: (_value, row) => (
      <ContactPointsDisplay
        points={row.contactPoints}
        kind="PHONE"
        compact
      />
    ),
  },
  {
    key: 'clientAddress',
    header: 'Address',
    sortable: true,
    render: (value, row) => {
      const addressLine = value
        ? (String(value).length > 50 ? String(value).substring(0, 50) + '...' : String(value))
        : '-';
      const locationParts = [
        row.clientCity,
        row.clientZipCode,
        row.clientState,
        row.clientCountryDescr?.pc_descr || (row.clientCountryId ? String(row.clientCountryId) : ''),
      ].filter(Boolean);

      return (
        <div className="min-w-0">
          <div>{addressLine}</div>
          {locationParts.length > 0 && (
            <div className={cn('text-xs truncate', mutedTextClass(colors))}>
              {locationParts.join(', ')}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'clientWebSite',
    header: 'Web',
    sortable: true,
    render: (value) => (
      value ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          title={String(value)}
          aria-label={`Open website ${String(value)}`}
          className={cn('inline-flex items-center', secondaryLinkClass(colors))}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 115.656 5.656l-1.5 1.5"
            />
          </svg>
        </a>
      ) : (
        '-'
      )
    ),
  },
  {
    key: 'clientVATNumber',
    header: 'Identification',
    sortable: true,
    render: (_value, row) => (
      <div className="min-w-0">
        <div>{row.gemiNumber || '-'}</div>
        {row.clientCode && (
          <div className={cn('inline-flex items-center gap-1 text-xs truncate', mutedTextClass(colors))}>
            <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 4h14a2 2 0 012 2v7H3V6a2 2 0 012-2z"
              />
            </svg>
            <span>{row.clientCode}</span>
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'clientPaymentTerms',
    header: 'Payment Terms',
    sortable: true,
    render: (value) => value ? `${value} days` : '-',
  },
  {
    key: 'clientTaxAuthority',
    header: 'Tax',
    sortable: true,
    render: (value) => value || '-',
  },
  {
    key: 'dateStarted',
    header: 'Date Started',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'sourceUrl',
    header: 'Source URL',
    sortable: true,
    render: (value) => (
      value ? (
        <a href={value} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      ) : (
        '-'
      )
    ),
  },
  {
    key: 'clientCreatedAt',
    header: 'Created At',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'clientUpdatedAt',
    header: 'Updated At',
    sortable: true,
    render: (value) => formatDate(value),
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: Client,
  formatDate: (dateString?: string) => string,
  _formatCurrency?: (amount?: number | null) => string,
  _mutate?: () => void,
  colors?: ThemeColors,
) => {
  return [
    {
      title: 'Basic Information',
      fields: [
        {
          label: 'Organization name',
          value: item.clientName || '-',
        },
        {
          label: 'Alternate name',
          value: item.clientName2 || '-',
          condition: !!item.clientName2,
        },
        {
          label: 'Reference code',
          value: item.clientCode || '-',
          condition: !!item.clientCode,
        },
        {
          label: 'VAT Number',
          value: displayVatNumber(item.clientVATNumber) || '-',
          condition: !!displayVatNumber(item.clientVATNumber),
        },
        {
          label: 'GEMI Number',
          value: item.gemiNumber || '-',
          condition: !!item.gemiNumber,
        },
      ],
    },
    {
      title: 'Contact Information',
      fields: [
        {
          label: 'Contacts',
          value: <OrganizationContactsDisplay points={item.contactPoints} />,
          condition: hasContactPoints(item.contactPoints, 'EMAIL'),
        },
        {
          label: 'Phones',
          value: (
            <ContactPointsDisplay
              points={item.contactPoints}
              kind="PHONE"
            />
          ),
          condition: hasContactPoints(item.contactPoints, 'PHONE'),
        },
        {
          label: 'Website',
          value: item.clientWebSite ? (
            <a
              href={item.clientWebSite}
              target="_blank"
              rel="noopener noreferrer"
              className={cn('hover:underline', primaryLinkClass(colors))}
            >
              {item.clientWebSite}
            </a>
          ) : (
            '-'
          ),
          condition: !!item.clientWebSite,
        },
      ],
    },
    {
      title: 'Address',
      fields: [
        {
          label: 'Street Address',
          value: item.clientAddress || '-',
          condition: !!item.clientAddress,
        },
        {
          label: 'City',
          value: item.clientCity || '-',
          condition: !!item.clientCity,
        },
        {
          label: 'Zip Code',
          value: item.clientZipCode || '-',
          condition: !!item.clientZipCode,
        },
        {
          label: 'State',
          value: item.clientState || '-',
          condition: !!item.clientState,
        },
      ],
      condition: !!(item.clientAddress || item.clientCity || item.clientZipCode || item.clientState),
    },
    {
      title: 'Additional Information',
      fields: [
        {
          label: 'Notes',
          value: item.clientNotes ? (
            <span className="whitespace-pre-wrap">{item.clientNotes}</span>
          ) : (
            '-'
          ),
          condition: !!item.clientNotes,
        },
        {
          label: 'Payment Terms',
          value: item.clientPaymentTerms ? `${item.clientPaymentTerms} days` : '-',
          condition: !!item.clientPaymentTerms,
        },
        {
          label: 'Created At',
          value: formatDate(item.clientCreatedAt),
        },
        {
          label: 'Last Updated',
          value: formatDate(item.clientUpdatedAt),
          condition: !!item.clientUpdatedAt,
        },
      ],
    },
    {
      title: 'Attachments',
      icon: '📎',
      fields: [
        {
          label: '',
          value: React.createElement(EntityAttachmentsSection, {
            downloadEndpoint: `/crm-clients/client/${item.clientId}/files`,
            files: item.FileAttachment || [],
            defaultDownloadName: `client_${item.clientId}_files.zip`,
            getViewFileUrl: (fileId) => `/crm-clients/client/${item.clientId}/files/${fileId}`,
          }),
        },
      ],
    },
    {
      title: 'Linked Emails',
      icon: '📧',
      fields: [
        {
          label: '',
          value: React.createElement(LinkedEmailsSection, { clientId: item.clientId }),
        },
      ],
    },
    {
      title: 'People',
      icon: '👤',
      fields: [
        {
          label: '',
          value: React.createElement(ClientContactsSection, { clientId: item.clientId }),
        },
      ],
    },
    {
      title: 'Activity Log',
      icon: '📋',
      fields: [
        {
          label: '',
          value: React.createElement(ActivityLogSection, { entityType: 'Client', entityId: item.clientId }),
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (_item: Client) => null;

// Helper function to get form sections
export const getFormSections = (): FormSection[] => {
  return [
    {
      title: 'Basic Information',
      gridCols: 2,
      fields: [
        {
          name: 'clientName',
          label: 'Organization name',
          type: 'text',
          required: true,
        },
        {
          name: 'clientName2',
          label: 'Alternate name',
          type: 'text',
        },
        {
          name: 'clientCode',
          label: 'Reference code',
          type: 'text',
        },
      ],
    },
    {
      title: 'Contact Information',
      gridCols: 2,
      fields: [
        {
          name: 'clientWebSite',
          label: 'Website',
          type: 'url',
        },
      ],
    },
    {
      title: 'Address Information',
      gridCols: 2,
      fields: [
        {
          name: 'clientAddress',
          label: 'Address',
          type: 'text',
          colSpan: 2,
        },
        {
          name: 'clientCity',
          label: 'City',
          type: 'text',
        },
        {
          name: 'clientZipCode',
          label: 'Zip Code',
          type: 'text',
        },
        {
          name: 'clientState',
          label: 'State',
          type: 'text',
        },
      ],
    },
    {
      title: 'Additional Information',
      gridCols: 2,
      fields: [
        {
          name: 'clientVATNumber',
          label: 'VAT Number',
          type: 'text',
        },
        {
          name: 'gemiNumber',
          label: 'GEMI Number',
          type: 'text',
        },
        {
          name: 'sourceUrl',
          label: 'Source URL',
          type: 'url',
        },
        {
          name: 'clientPaymentTerms',
          label: 'Payment Terms',
          type: 'number',
        },
        {
          name: 'clientNotes',
          label: 'Notes',
          type: 'textarea',
          colSpan: 2,
        },
      ],
    },
  ];
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'settings':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'upload':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: Client,
  onDelete: (id: number) => void,
  colors?: ThemeColors,
  listContext?: { onOpenSummary?: (item: Client) => void },
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <div className="flex items-center space-x-2 flex-nowrap">
      {listContext?.onOpenSummary && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            listContext.onOpenSummary?.(item);
          }}
          className={summaryActionClass(colors)}
          title="Summary (all details & related modules)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </button>
      )}
      <Link
        to={moduleConfig.module.route.detail(item.clientId)}
        className={viewActionClass(colors)}
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </Link>
      {canEdit && (
        <Link
          to={moduleConfig.module.route.edit(item.clientId)}
          className={editActionClass(colors)}
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </Link>
      )}
      {canDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.clientId);
          }}
          className={deleteActionClass(colors)}
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Helper function to get initial form data
export const getInitialFormData = (): ClientFormData => {
  return {
    clientName: '',
    clientName2: '',
    clientCode: '',
    contactPerson: '',
    contactPosition: '',
    clientAddress: '',
    clientCity: '',
    clientZipCode: '',
    clientState: '',
    clientWebSite: '',
    organizationContacts: [emptyOrganizationContact(true)],
    contactPhones: [],
    clientNotes: '',
    clientVATNumber: '',
    clientPaymentTerms: undefined,
    clientTaxAuthority: undefined,
    gemiNumber: '',
    sourceUrl: '',
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Client): ClientFormData => {
  return {
    clientName: item.clientName || '',
    clientName2: item.clientName2 || '',
    clientCode: item.clientCode || '',
    contactPerson: item.contactPerson || '',
    contactPosition: item.contactPosition || '',
    clientAddress: item.clientAddress || '',
    clientCity: item.clientCity || '',
    clientZipCode: item.clientZipCode || '',
    clientState: item.clientState || '',
    clientWebSite: item.clientWebSite || '',
    organizationContacts: groupContactPointsToOrganizationContacts(item.contactPoints),
    contactPhones: contactPointsToRows(item.contactPoints, 'PHONE'),
    clientNotes: item.clientNotes || '',
    clientVATNumber: vatForFormField(item.clientVATNumber),
    clientPaymentTerms: item.clientPaymentTerms,
    clientTaxAuthority: item.clientTaxAuthority,
    gemiNumber: item.gemiNumber || '',
    sourceUrl: item.sourceUrl || '',
  };
};

export type ModuleConfig = typeof moduleConfig;
